/**
 * StepLoanType — Step 2 of the loan application form (PR #26).
 *
 * Single-decision step: borrower picks one of the 11 active loan types.
 * The choice flows into `formData.loan_details.loan_type` (existing field) so
 * downstream steps and the post-submission checklist auto-creator both pick
 * it up. The choice gates submission server-side.
 *
 * Loan-type list is fetched from the public `/api/loan-types` endpoint so
 * additions to LOAN_TYPES on the backend take effect without a frontend
 * redeploy.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Briefcase, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";

export const StepLoanType = ({ formData, updateFormData, API }) => {
  const [loanTypes, setLoanTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const selected = formData?.loan_details?.loan_type || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/loan-types`);
        if (!cancelled) {
          setLoanTypes(res.data?.loan_types || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Could not load loan types — please refresh the page");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  const handleSelect = (id) => {
    const nextLoanDetails = { ...(formData.loan_details || {}), loan_type: id };
    updateFormData("loan_details", nextLoanDetails);
  };

  return (
    <div className="space-y-6" data-testid="step-loan-type">
      <div>
        <h2 className="text-2xl font-semibold text-[#021d49] mb-1 flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-[#bd9a59]" />
          Loan Type
        </h2>
        <p className="text-sm text-[#64748b]">
          Choose the loan type that best fits this transaction. We'll tailor the document checklist to match.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#64748b]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading loan types…
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">{error}</div>
      )}

      {!loading && !error && (
        <div>
          <Label className="text-sm font-medium text-[#021d49] mb-3 block">
            What kind of loan is this? <span className="text-red-500">*</span>
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {loanTypes.map((lt) => {
              const isSelected = selected === lt.id;
              return (
                <button
                  key={lt.id}
                  type="button"
                  onClick={() => handleSelect(lt.id)}
                  className={`text-left rounded-lg border-2 px-4 py-3 transition-all ${
                    isSelected
                      ? "border-[#bd9a59] bg-[#bd9a59]/10 text-[#021d49] shadow-sm"
                      : "border-gray-200 bg-white hover:border-[#bd9a59]/50 hover:bg-[#bd9a59]/5"
                  }`}
                  data-testid={`loan-type-${lt.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-0.5 inline-block w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                        isSelected ? "border-[#bd9a59] bg-[#bd9a59]" : "border-gray-300 bg-white"
                      }`}
                    />
                    <span className="font-medium text-sm">{lt.label}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <p className="text-xs text-[#64748b] mt-4 italic">
            You can change this on later steps if needed. Your document checklist updates accordingly when you submit.
          </p>
        </div>
      )}
    </div>
  );
};

export default StepLoanType;
